import { sanitizeFileName } from '@/common/utils/fileSystemAccess';
import {
  blueprintSourceExtension,
  getBlueprintFileError,
} from '@/common/utils/blueprintFilePolicy';

// Include queued transfers when choosing a filename, before they reach disk.
const reservedPaths = new Set<string>();

function safeName(value: string, maxBytes = 230): string {
  // Filesystem names must not contain control characters.
  let name = sanitizeFileName(
    value
      .normalize('NFKC')
      // eslint-disable-next-line no-control-regex
      .replace(/[\u0000-\u001f\u007f\u202a-\u202e\u2066-\u2069]/g, '')
  );
  // Leave space for an extension and copy suffix within a 255-byte component.
  const characters = Array.from(name);
  const encoder = new TextEncoder();
  while (
    characters.length &&
    encoder.encode(characters.join('')).length > maxBytes
  )
    characters.pop();
  name = characters.join('').replace(/[. ]+$/, '') || 'untitled';
  if (/^(con|prn|aux|nul|com[1-9]|lpt[1-9])(?:\.|$)/i.test(name))
    name = `_${name}`;
  return name;
}

export function blueprintExtension(fileName: string): string {
  return blueprintSourceExtension(fileName);
}

export function blueprintFileName(
  title: string,
  sourceFileName = 'blueprint.adg',
  version?: number,
  copy = 1
): string {
  const extension = blueprintExtension(sourceFileName);
  const stem =
    extension && title.toLowerCase().endsWith(extension.toLowerCase())
      ? title.slice(0, -extension.length)
      : title;
  const suffix = `${version ? ` v${version}` : ''}${copy === 1 ? '' : ` (${copy})`}${extension}`;
  // Allow a leading underscore for Windows reserved names within 255 bytes.
  const budget = Math.min(230, 254 - new TextEncoder().encode(suffix).length);
  if (budget < 8) throw new Error('Blueprint file extension is too long.');
  // Titles are display text; do not let their dots create a new file type or
  // a blocked double extension that prevents native drag-out.
  let name = safeName(stem, budget);
  if (!extension || getBlueprintFileError(`${name}${suffix}`))
    name = safeName(name.replace(/\./g, ' '), budget);
  return `${name}${suffix}`;
}

export async function reserveBlueprintDownloadPath(
  root: string,
  creator: string,
  title: string,
  getFileInfo: (path: string) => Promise<{ success: boolean; error?: string }>,
  sourceFileName = 'blueprint.adg',
  version?: number
): Promise<{ relative: string; release: () => void }> {
  const directory = safeName(creator);
  for (let copy = 1; ; copy++) {
    const relative = `${directory}/${blueprintFileName(title, sourceFileName, version, copy)}`;
    const fullPath = `${root}/${relative}`;
    // Conservatively avoid collisions on case-insensitive filesystems too.
    const key = fullPath.normalize('NFC').toLowerCase();
    if (reservedPaths.has(key)) continue;
    reservedPaths.add(key);
    try {
      const info = await getFileInfo(fullPath);
      if (info.success) {
        reservedPaths.delete(key);
        continue;
      }
      if (!info.error?.includes('ENOENT')) {
        throw new Error(
          info.error || 'Could not check the blueprint download location.'
        );
      }
      return {
        relative,
        release: () => {
          reservedPaths.delete(key);
        },
      };
    } catch (error) {
      reservedPaths.delete(key);
      throw error;
    }
  }
}
